/**
 * People — the staff-visible Students & Clients directory (PR-000016).
 *
 * All staff (STAFF_ROLE_NAMES) may VIEW students/clients here, including contact details + ARN.
 * Read-only for everyone; editing stays on the admin-only /admin Users manager (admins get an
 * "Edit in Admin" affordance below). The server gate on GET /api/students/directory is the real
 * authority — this page is UX only (route-gated in App.tsx).
 *
 * PII containment (hard requirement): the directory payload carries email/mobile/ARN. It is
 * fetched on mount, ONLINE-ONLY, held in component state ONLY, and is NEVER written to RefData,
 * saveRefDataToCache, or any IndexedDB store. Do not route it through the offline reference cache.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { personDisplayLabel } from '@avsuite/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import { Spinner, ErrorBox } from '../components/ops-board/atoms';
import { C, F } from '../components/ops-board/theme';

// Local, page-scoped type — deliberately NOT added to opsBoardTypes/RefData, so this PII shape can
// never be threaded into the picker feed or the offline cache. Mirrors listStudentsDirectory.
interface DirectoryPerson {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  preferred_name: string | null;
  pilot_code: string | null;
  training_stage: string | null;
  home_base_id: string | null;
  is_active: boolean;
  email: string | null;
  mobile: string | null;
  arn: string | null;
  arn_pending: boolean;
  kind: 'student' | 'client';
}

type KindFilter = 'all' | 'student' | 'client';

const card: React.CSSProperties = {
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
};
const badge = (bg: string, color: string): React.CSSProperties => ({
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
  color, background: bg, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap',
});
const control: React.CSSProperties = {
  fontFamily: F, fontSize: 13, color: C.text, background: C.surface,
  border: `1px solid ${C.borderMed}`, borderRadius: 7, padding: '7px 10px',
};

// Human ARN cell: an issued ARN wins; otherwise "Pending" iff flagged, else "—".
function arnDisplay(p: DirectoryPerson): string {
  if (p.arn && p.arn.trim()) return p.arn;
  return p.arn_pending ? 'Pending' : '—';
}

export default function People() {
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');

  const [rows, setRows] = useState<DirectoryPerson[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [showInactive, setShowInactive] = useState(false); // admins only (server enforces)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ONLINE-ONLY fetch, straight into component state. No cache read/write anywhere. include_inactive
  // is only honoured server-side for admins; we still pass it only when an admin has opted in.
  const load = useCallback(async (inactive: boolean) => {
    setLoading(true);
    setError(null);
    const qs = inactive ? '?include_inactive=true' : '';
    const res = await apiFetch<DirectoryPerson[]>(`/api/students/directory${qs}`).catch(() => null);
    if (!res) { setError('Could not reach the server. The directory is online-only — reconnect and retry.'); setLoading(false); return; }
    if (res.error) { setError(res.error.message); setLoading(false); return; }
    setRows(res.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void load(isAdmin && showInactive); }, [load, isAdmin, showInactive]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((p) => {
      if (kindFilter !== 'all' && p.kind !== kindFilter) return false;
      if (!q) return true;
      const hay = [p.full_name, p.preferred_name, p.company, p.pilot_code, p.first_name, p.last_name]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, kindFilter]);

  const counts = useMemo(() => {
    const list = rows ?? [];
    return { total: list.length, students: list.filter((p) => p.kind === 'student').length, clients: list.filter((p) => p.kind === 'client').length };
  }, [rows]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
      <AppHeader context="Students & clients" />
      <main style={{ padding: '20px 18px 60px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Students &amp; clients</h1>
          {rows && (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {counts.total} total · {counts.students} students · {counts.clients} clients
            </span>
          )}
          {isAdmin && (
            <Link
              to="/admin?section=users"
              style={{ marginLeft: 'auto', ...badge(C.accentLight, C.accent), textDecoration: 'none', padding: '6px 12px', fontSize: 12 }}
            >
              Edit in Admin →
            </Link>
          )}
        </div>

        {/* Filters */}
        <div style={{ ...card, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '12px 14px', marginBottom: 14 }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, company, code…"
            aria-label="Search students and clients"
            style={{ ...control, flex: '1 1 240px', minWidth: 200 }}
          />
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as KindFilter)} aria-label="Filter by kind" style={control}>
            <option value="all">All</option>
            <option value="student">Students</option>
            <option value="client">Clients</option>
          </select>
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textDim, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          )}
        </div>

        {loading && <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner label="Loading directory…" /></div>}
        {!loading && error && <ErrorBox message={error} onRetry={() => void load(isAdmin && showInactive)} />}

        {!loading && !error && rows && (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtered.length === 0 && (
              <div style={{ padding: 18, color: C.textMuted, fontSize: 13 }}>No students or clients match.</div>
            )}
            {filtered.map((p) => {
              const name = personDisplayLabel({ full_name: p.preferred_name ?? p.full_name, company: p.company });
              const expanded = expandedId === p.id;
              return (
                <div key={p.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : p.id)}
                    aria-expanded={expanded}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                      background: expanded ? C.surfaceHover : 'none', border: 'none', cursor: 'pointer',
                      padding: '11px 14px', fontFamily: F, color: C.text,
                    }}
                  >
                    <span style={{ fontSize: 11, color: C.textMuted, width: 12, flexShrink: 0 }}>{expanded ? '▾' : '▸'}</span>
                    <span style={{ flex: '2 1 180px', minWidth: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                      {p.pilot_code && <span style={{ color: C.textMuted, fontWeight: 400 }}> · {p.pilot_code}</span>}
                    </span>
                    <span style={badge(p.kind === 'student' ? C.accentLight : C.purpleLight, p.kind === 'student' ? C.accent : C.purple)}>
                      {p.kind}
                    </span>
                    <span style={{ flex: '1 1 140px', fontSize: 12, color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.company ?? ''}
                    </span>
                    <span style={{ flex: '1 1 120px', fontSize: 12, color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.kind === 'student' ? (p.training_stage ?? '') : ''}
                    </span>
                    {!p.is_active && <span style={badge(C.pill, C.textMuted)}>Inactive</span>}
                  </button>
                  {expanded && (
                    <dl style={{ margin: 0, padding: '4px 14px 14px 36px', display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', fontSize: 13 }}>
                      <dt style={{ color: C.textMuted }}>Email</dt>
                      <dd style={{ margin: 0 }}>{p.email ? <a href={`mailto:${p.email}`} style={{ color: C.accent }}>{p.email}</a> : '—'}</dd>
                      <dt style={{ color: C.textMuted }}>Mobile</dt>
                      <dd style={{ margin: 0 }}>{p.mobile ? <a href={`tel:${p.mobile}`} style={{ color: C.accent }}>{p.mobile}</a> : '—'}</dd>
                      <dt style={{ color: C.textMuted }}>ARN</dt>
                      <dd style={{ margin: 0, color: !p.arn && p.arn_pending ? C.amber : C.text }}>{arnDisplay(p)}</dd>
                    </dl>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
